/*---------------------------------------------------------------------------------------------
 *  Copyright (c) Microsoft Corporation. All rights reserved.
 *  Licensed under the MIT License. See License.txt in the project root for license information.
 *--------------------------------------------------------------------------------------------*/

import * as React from 'react';

import { ActiveCartProductsInput, getActiveCartProductsAction } from '@msdyn365-commerce-modules/retail-actions';
import { IImageDimension } from '@msdyn365-commerce/core';
import { CartLine, CommerceProperty, SimpleProduct } from '@msdyn365-commerce/retail-proxy';
import { observable, reaction } from 'mobx';
import { observer } from 'mobx-react';
import { SubscriptionCommerceValues } from '../../common/subscription-commerce.values';
import { BusLoader, LoadBus } from '../../Utilities/event-bus';
import { getSubscriptionValues, updateSubscription } from '../../Utilities/subscription-manager';
import { BottleCountDisplay } from './components/bottle-count-display.component';
import { BoxEditComponent } from './components/box-edit.component';
import { CartDisplayComponent } from './components/cart-display.component';
import { SubscriptionBannerDisplayComponent } from './components/subscription-banner-display.component';
import { ISubscriptionsMinicartData } from './subscriptions-minicart.data';
import { IFrequencyTableData, ISubscriptionsMinicartProps } from './subscriptions-minicart.props.autogenerated';
export interface ISubscriptionsMinicartViewProps extends ISubscriptionsMinicartProps<ISubscriptionsMinicartData> {
    editBoxComponent: React.ReactNode;
    bottleCountComponent: React.ReactNode;
    cartDisplaySummaryComponent: React.ReactNode;
    subscriptionBannerDisplayComponent: React.ReactNode;
    baseProps: {
        boxName: string;
        frequency: IFrequencyTableData;
    };
}

export interface IWineBoxLine {
    line: CartLine;
    product: SimpleProduct;
}

interface IImageSettingsViewports {
    xs: IImageDimension;
    sm?: IImageDimension | undefined;
    md?: IImageDimension | undefined;
    lg?: IImageDimension | undefined;
    xl?: IImageDimension | undefined;
}

/**
 *
 * SubscriptionsMinicart component
 * @extends {React.Component<ISubscriptionsMinicartProps<ISubscriptionsMinicartData>>}
 */
@LoadBus('product-component-wrapper')
@observer
class SubscriptionsMinicart extends React.Component<ISubscriptionsMinicartProps<ISubscriptionsMinicartData>> {

    @observable private _boxName: string;
    @observable private _frequencyId: string;
    private bus: BusLoader | undefined;
    private _eventId: number;

    constructor(props: ISubscriptionsMinicartProps<ISubscriptionsMinicartData>) {
        super(props);

        this._onBoxNameChange = this._onBoxNameChange.bind(this);
        this._onFrequencyChange = this._onFrequencyChange.bind(this);

        const modifiedImageSettings = props.config.imageSettings;
        if (modifiedImageSettings) {
            if (modifiedImageSettings.viewports) {
                modifiedImageSettings.viewports = this.usePNGImageSetting(modifiedImageSettings.viewports);
                props.config.imageSettings = modifiedImageSettings;
            }
        }

        this._boxName = '';
        this._frequencyId = '';

        reaction(
            () => { return this.props.data.cart.result?.cart.CartLines; },
            () => {
                this.props.data.products.run(async (exec) => {
                    exec(
                        await getActiveCartProductsAction(
                            new ActiveCartProductsInput(),
                            this.props.context.actionContext
                        )
                    );
                });
            }
        );

        reaction(
            () => { return this.props.data.cart.result?.cart; },
            () => {
                const { name: newName, frequency: newFreq } = getSubscriptionValues(this.props.data.cart.result?.cart);
                this._boxName = newName || '';
                if (newFreq) { this._frequencyId = newFreq; }
            }
        );

        this._eventId = this.bus?.subscribe('finished-adding-to-cart', () => {
            this.props.data.cart.result && this.props.data.cart.result.refreshCart({});
        }).id!;

        // sets default subscription box name if unset
        props.data.cart.then(() => {
            const values = getSubscriptionValues(this.props.data.cart.result?.cart);
            if (!values.name) {
                // tslint:disable-next-line: no-floating-promises
                void this._onBoxNameChange(SubscriptionCommerceValues.SUBSCRIPTION_DEFAULT_NAME);
            }
        }).catch(e => this.props.telemetry.exception(e));
    }

    public componentWillUnmount(): void {
        this.bus?.unsubscribe(this._eventId);
    }

    public render(): JSX.Element | null {
        if (!this.props.data.cart.result) { return <div />; }
        const values = getSubscriptionValues(this.props.data.cart.result?.cart);
        const renderProps: ISubscriptionsMinicartViewProps = {
            ...this.props,
            baseProps: {
                boxName: this._boxName,
                frequency: this.props.config.frequencyTable?.find(freq => this._frequencyId === freq.id)!
            },
            subscriptionBannerDisplayComponent: (
                <SubscriptionBannerDisplayComponent
                    resources={this.props.resources}
                    subscriptionName={this._boxName || values.name!}
                />
            ),
            editBoxComponent: (
                <BoxEditComponent
                    onBoxNameChange={this._onBoxNameChange}
                    onFrequencyChange={this._onFrequencyChange}
                    initialValues={{ name: this._boxName || values.name, frequencyId: this._frequencyId || values.frequency }}
                    props={this.props}
                />
            ),
            bottleCountComponent: (
                <BottleCountDisplay
                    subscriptionProps={this.props}
                />
            ),
            cartDisplaySummaryComponent: (
                <CartDisplayComponent
                    subscriptionsProps={this.props}
                />
            )
        };

        return this.props.renderView(renderProps);
    }

    public usePNGImageSetting = (viewports: IImageSettingsViewports): IImageSettingsViewports => {
        const modifiedViewports = viewports;
        if (modifiedViewports?.xs?.q) {
            modifiedViewports.xs.q = modifiedViewports.xs.q.replace('f=jpg', 'f=png');
        }
        if (modifiedViewports?.sm?.q) {
            modifiedViewports.sm.q = modifiedViewports.sm.q.replace('f=jpg', 'f=png');
        }
        if (modifiedViewports?.md?.q) {
            modifiedViewports.md.q = modifiedViewports.md.q.replace('f=jpg', 'f=png');
        }
        if (modifiedViewports?.lg?.q) {
            modifiedViewports.lg.q = modifiedViewports.lg.q.replace('f=jpg', 'f=png');
        }
        if (modifiedViewports?.xl?.q) {
            modifiedViewports.xl.q = modifiedViewports.xl.q.replace('f=jpg', 'f=png');
        }

        return modifiedViewports;
    };

    private async _pushUpdateToCart(updates: { key: string; value: string }[]): Promise<void> {
        // we need to parse this form's format into how the cart likes to
        // handle things
        const cartChanges: CommerceProperty[] = updates.map(change => {
            return {
                Key: change.key,
                Value: {
                    StringValue: change.value
                }
            };
        });

        await updateSubscription(this.props.data.cart, cartChanges, this.props.context);
    }

    private async _onBoxNameChange(newName: string): Promise<void> {
        this._boxName = newName;
        await this._pushUpdateToCart([
            {
                key: SubscriptionCommerceValues.SUBSCRIPTION_NAME,
                value: this._boxName
            },
            {
                key: SubscriptionCommerceValues.SUBSCRIPTION_FREQUENCY,
                value: this._frequencyId
            },
        ]);
    }

    private async _onFrequencyChange(frequency: IFrequencyTableData): Promise<void> {
        this._frequencyId = frequency.id;
        await this._pushUpdateToCart([
            {
                key: SubscriptionCommerceValues.SUBSCRIPTION_FREQUENCY,
                value: this._frequencyId
            }
        ]);
    }

}

export default SubscriptionsMinicart;
